// The composition catalog: a CTD §3.2.P.1, Description and Composition of the Drug
// Product. Its counterpart on the backend is the `COMPOSITION` entry in
// services/cer/templates.js.
//
// Spare even next to the stability catalog, and deliberately so: the engine derives
// everything it can. A variant's formula, label, strength and status are all
// discovered from the composition tables and bound to its name, and sending any of
// them is rejected outright — so the whole form is two authored facts plus the four
// document lists.
import { GENERAL_SECTION } from "../fields";

// The four retrieval steps. None is optional: each reads *only* from its own list,
// and an empty one does not fail the run — it produces a COMPLETED run whose
// regulatory document reads "[Data not available]". A silently wrong document is
// worse than a refused one, which is why all four are gated before submit.
const SECTION_CATALOG = [
    {
        key: "ingredients",
        label: "Unit Composition",
        description:
            "The unit-composition table — quantities per unit. This is also where each variant's identity is discovered, so it feeds Table 1 and the variant matching both.",
    },
    {
        key: "ingredient_function",
        label: "Ingredient Function",
        description:
            "The per-ingredient Function column. Commonly the same file as Unit Composition — one signed confirmation letter usually carries the quantities and the Function column in a single table, and assigning it to both lists is normal rather than an edge case.",
    },
    {
        key: "packaging",
        label: "Container Closure System",
        description: "Becomes a section of the generated document.",
    },
    {
        key: "dosage_form",
        label: "Dosage Form Description",
        description: "Dosage-form description prose, used for the per-variant sentences.",
    },
];

const SECTIONS = SECTION_CATALOG.map((s) => s.key);

// There is nothing to select — all four sections are always generated — so the
// wizard goes straight from the product facts to the four document lists.
const STEPS = [
    { key: "general", title: "Product Information" },
    { key: "details", title: "Configure" },
];

const BLOCK_TITLES = {
    "general:product": "Drug Product",
};

const BLOCK_DESCRIPTIONS = {
    "general:product":
        "The two things no source document can state. Everything else — each variant's formula, label, strength and packaging status — is derived from these and from the documents you assign below.",
};

// Both are `[composition]` keys and carry `group: "composition"`, which routes them
// into that config table rather than into the metadata bag. This template asks for
// nothing out of `[metadata]`: no part of §3.2.P.1 reads it.
const FIELDS = [
    {
        section: GENERAL_SECTION,
        block: "product",
        group: "composition",
        name: "product_name",
        label: "Product Name",
        type: "text",
        description: "The one drug product this document describes.",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        block: "product",
        group: "composition",
        name: "variants",
        label: "Variants",
        type: "string_list",
        description:
            'One entry per column of the unit-composition table, each the variant\'s regulatory display name. Start each name with the product name — the engine strips that prefix to get the column header, so "Nitroglycerin Invisipatch 10 mg/16 h" gives a clean "10 mg/16 h". Names must be distinct.',
        required: true,
    },
];

// Panel-level warnings for the two cases that produce a wrong-looking document
// rather than an error. Both mirror a check upstream makes: the prefix rule earns a
// warning on the accepted run, and a duplicate name is refused outright — surfacing
// them here is what stops the user finding out after the fact.
const advisories = (sectionKey, values) => {
    if (sectionKey !== GENERAL_SECTION) {
        return [];
    }

    const out = [];
    const productName = (values.product_name || "").trim();
    const names = (values.variants || []).map((n) => (n || "").trim()).filter(Boolean);

    const duplicates = [...new Set(names.filter((n, i) => names.indexOf(n) !== i))];
    if (duplicates.length) {
        out.push({
            type: "warning",
            message: "Variant names must be distinct",
            description: `Each variant is one column of the unit-composition table, so a repeated name is rejected before generation starts. Duplicated: ${duplicates
                .map((n) => `"${n}"`)
                .join(", ")}.`,
        });
    }

    // Only worth saying once the product name exists to be compared against.
    const unprefixed = productName ? names.filter((n) => !n.startsWith(productName)) : [];
    if (unprefixed.length) {
        out.push({
            type: "warning",
            message: "Some variant names will not shorten to a column header",
            description: `The engine derives each table column header by stripping "${productName}" from the variant name. These do not extend it, so their header will be the full name: ${unprefixed
                .map((n) => `"${n}"`)
                .join(", ")}.`,
        });
    }

    return out;
};

export default {
    id: "composition",
    steps: STEPS,
    sectionCatalog: SECTION_CATALOG,
    fields: FIELDS,
    blockTitles: BLOCK_TITLES,
    blockSections: {},
    blockDescriptions: BLOCK_DESCRIPTIONS,
    // Nothing here is scoped by screened literature.
    sectionArticleType: {},
    sectionsWithoutDocuments: new Set(),
    // Every list is required, so none may be left empty.
    optionalSections: new Set(),
    orderedSectionKey: null,
    nonMetadataKeys: new Set(),
    // All four, always: the section picker is skipped and these are treated as
    // selected from the first render, so the config always carries all four keys and
    // the backend can hold the run to them.
    fixedSections: SECTIONS,
    payloadTables: ["composition"],
    // Both are a 400 under this template.
    emitsDeviceFamily: false,
    hasArticles: false,
    advisories,
};
